import React, { useState } from 'react'
import { Button } from 'react-bootstrap'
import ReactSelect, { Option } from 'react-select'

import authorization from 'live/services/authorization'
import i18n from 'live/services/i18n'

import { severityMock } from 'plugin-codaf-risk-ext-bowtie/BowtieAlarms/mocks/mocks'
import { updateSeenBy } from 'plugin-codaf-risk-ext-bowtie/services'
import { IBowtie } from 'plugin-codaf-risk-ext-bowtie/types'
import { IAlarmEvent } from 'plugin-codaf-risk-ext-bowtie/types/alarms'

import { IOverviewAlarmEvent, IOverviewAsset } from '../../AssetSelector'
import { AlarmCard } from './components/AlarmCard/AlarmCard'
import { AlarmListContainer, CardList } from './styles'

interface IProps {
    asset: IOverviewAsset
    setShowAlarmModal: (k: boolean) => void
    setShowAlarmsDashboard: (k: boolean) => void
    setShowAlarmDetails: (k: boolean) => void
    alarms: Map<number, IAlarmEvent>
    setAlarmData: (k: IOverviewAlarmEvent) => void
    setSelectedBowtie: (k: number) => void
    alarmBowties: IBowtie[]
}

interface IOperationData {
    id: number
    description: string
}
interface ISeverityData {
    id: number
    name: string
}

interface IOrder {
    order: boolean
}

export const AlarmList: React.FC<IProps> = ({
    asset,
    setShowAlarmModal,
    setShowAlarmsDashboard,
    setShowAlarmDetails,
    alarms,
    setAlarmData,
    setSelectedBowtie,
    alarmBowties
}) => {
    const [showSelectDropdown, setShowSelectDropdown] = useState<boolean>(false)
    const [filterOperation, setFilterOperation] = useState<IOperationData[]>([])
    const [severityFilter, setSeverityFilter] = useState<ISeverityData[]>([])
    const [filterSeverityAlarms, setFilterSeverityAlarms] = useState<Set<string>>(new Set<string>())
    const [filterOperationAlarms, setFilterOperationAlarms] = useState<Set<number>>(new Set<number>())

    const [order, setOrder] = useState<IOrder>({ order: true })

    const handleClick = (): void => {
        setAlarmData(undefined)
        setShowAlarmsDashboard(true)
    }

    return (
        <AlarmListContainer>
            <div className="alarm-list-header">
                <div className="list-header-first-row">
                    <div className="first-row-title">
                        <i className="far fa-bell" />
                        {asset?.name ?? 'RESOURCE_NAME'}
                    </div>
                    <i className="far fa-times" onClick={() => setShowAlarmModal(false)} />
                </div>
                <div className="list-header-detected-alarms">
                    {i18n('There are')} {asset.overviewAlarms.length} {i18n('detected alarms')}
                </div>
            </div>
            <div className="alarm-list-filter">
                <div className="list-filter first-row">
                    <div className="first-row-filter-buttons">
                        <div
                            className="filter-button"
                            onClick={() => {
                                setShowSelectDropdown(old => !old)
                            }}
                        >
                            <div className="icon-label-bundle">
                                <i className="far fa-filter" />
                                {i18n('Filter Operation and Filter Severity')}
                            </div>
                            {showSelectDropdown ? (
                                <i className="fas fa-caret-up" />
                            ) : (
                                <i className="fas fa-caret-down" />
                            )}
                        </div>
                        <div
                            className="order-button"
                            onClick={() => {
                                setOrder({
                                    order: !order.order
                                })
                                setShowSelectDropdown(false)
                            }}
                        >
                            <i className="fal fa-sort-alt" />
                        </div>
                    </div>
                    <Button
                        className="no-outline-button"
                        style={{ width: '120px' }}
                        bsStyle="primary"
                        onClick={() => handleClick()}
                    >
                        {i18n('Go to Alarms')}
                    </Button>
                </div>
                <div className="list-filter second-row">
                    <span className="selected-filter">{i18n('Selected Filters: ')}</span>
                    <div className={'tags-row'}>
                        {filterOperation?.map((operation, index) => (
                            <div key={index} className={'selected-tag'}>
                                {operation.description}
                                <i
                                    className="fas fa-times"
                                    onClick={() => {
                                        setFilterOperation(filterOperation.filter(option => option.id !== operation.id))
                                        filterOperationAlarms.delete(operation.id)
                                    }}
                                />
                            </div>
                        ))}
                        {severityFilter?.map((severity, index) => (
                            <div key={index} className={'selected-tag'}>
                                {i18n(severity.name)}
                                <i
                                    className="fas fa-times"
                                    onClick={() => {
                                        setSeverityFilter(severityFilter.filter(option => option.id !== severity.id))
                                        filterSeverityAlarms.delete(severity.name.toUpperCase())
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                    <div className="clear">
                        <span
                            className={'clear-label'}
                            onClick={() => {
                                setSeverityFilter([])
                                setFilterOperation([])
                                filterOperationAlarms.clear()
                                filterSeverityAlarms.clear()
                            }}
                        >
                            {i18n('Clear all')}
                        </span>
                    </div>
                </div>
                {showSelectDropdown && (
                    <div className="select-dropdown">
                        <label>{i18n('Operation')}</label>
                        <ReactSelect
                            placeholder={`${i18n('Select')}...`}
                            options={asset?.operations
                                .map(operation => ({
                                    label: operation.description,
                                    value: operation.id
                                }))
                                .filter(option =>
                                    (filterOperation ?? []).every(operation => operation.id !== option.value)
                                )}
                            onChange={(e: Option<number>) => {
                                const selectedOperation = asset?.operations?.find(operation => operation.id === e.value)

                                const newOperationFilter: IOperationData = {
                                    id: selectedOperation.id,
                                    description: selectedOperation.description
                                }

                                setFilterOperation([...filterOperation, newOperationFilter])
                                selectedOperation.bowtieViews.map(bowtieView =>
                                    filterOperationAlarms.add(bowtieView.bowtie.id)
                                )
                            }}
                        />
                        <label>{i18n('Severity')}</label>
                        <ReactSelect
                            placeholder={`${i18n('Select')}...`}
                            options={severityMock
                                .map(severity => ({ label: i18n(severity.name), value: severity.id }))
                                .filter(option =>
                                    (severityFilter ?? []).every(severity => severity.id !== option.value)
                                )}
                            onChange={(e: Option<number>) => {
                                const selectedSeverity = severityMock.find(severity => severity.id === e.value)

                                const newSeverityFilter: ISeverityData = {
                                    name: selectedSeverity.name,
                                    id: selectedSeverity.id
                                }

                                setSeverityFilter([...severityFilter, newSeverityFilter])
                                filterSeverityAlarms.add(newSeverityFilter.name.toUpperCase())
                            }}
                        />
                    </div>
                )}
            </div>
            <CardList>
                {asset?.overviewAlarms
                    .filter(el =>
                        filterSeverityAlarms.size === 0 || filterOperationAlarms.size === 0
                            ? filterOperationAlarms.size === 0 && filterSeverityAlarms.size > 0
                                ? filterSeverityAlarms.has(el.status)
                                : filterSeverityAlarms.size === 0 && filterOperationAlarms.size > 0
                                ? el.bowties_id.filter(bowtieId => filterOperationAlarms.has(bowtieId)).length > 0
                                : el
                            : filterSeverityAlarms.has(el.status) &&
                              el.bowties_id.filter(bowtieId => filterOperationAlarms.has(bowtieId)).length > 0
                    )
                    .sort((a, b) => {
                        if (order.order) {
                            if (a.timestamp > b.timestamp) return -1
                            if (a.timestamp < b.timestamp) return 1

                            return 0
                        } else {
                            if (a.timestamp < b.timestamp) return -1
                            if (a.timestamp > b.timestamp) return 1

                            return 0
                        }
                    })
                    .map((alarm, index) => {
                        return (
                            <AlarmCard
                                key={alarm.id}
                                onCardClick={() => {
                                    setShowAlarmDetails(old => !old)

                                    setAlarmData(alarm)
                                }}
                                alarm={alarm}
                                type={'overview'}
                                asset={asset}
                                setSelectedBowtie={setSelectedBowtie}
                                alarmBowties={alarmBowties}
                                setAlarmData={setAlarmData}
                            />
                        )
                    })}
            </CardList>
        </AlarmListContainer>
    )
}
